package com.example.socialmediaapp.ui.screens

import androidx.compose.animation.core.*
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.lazy.*
import androidx.compose.foundation.lazy.staggeredgrid.*
import androidx.compose.foundation.pager.*
import androidx.compose.foundation.shape.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.draw.*
import androidx.compose.ui.graphics.*
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.*
import androidx.compose.ui.text.style.*
import androidx.compose.ui.unit.*
import coil3.compose.AsyncImage
import com.example.socialmediaapp.currentTimeMillis
import com.example.socialmediaapp.data.models.Post
import com.example.socialmediaapp.data.models.Short
import com.example.socialmediaapp.data.models.StoryGroup
import com.example.socialmediaapp.network.FeedRepository
import com.example.socialmediaapp.network.PostRepository
import com.example.socialmediaapp.network.StoriesRepository
import com.example.socialmediaapp.ui.theme.AppColors
import com.example.socialmediaapp.utils.TimeFormatter
import kotlinx.coroutines.launch

// ============================================
// HOME FEED SCREEN
// ============================================

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun FeedScreen(
    feedRepository: FeedRepository? = null,
    storiesRepository: StoriesRepository? = null,
    postRepository: PostRepository? = null,
    onProfileClick: () -> Unit = {},
    onSearchClick: () -> Unit = {},
    onNotificationsClick: () -> Unit = {},
    onCreateClick: () -> Unit = {},
    onAuthClick: () -> Unit = {},
    isGuest: Boolean = true
) {
    val scope = rememberCoroutineScope()
    var selectedTab by remember { mutableStateOf(0) }
    var showCreateSheet by remember { mutableStateOf(false) }
    var showFeedSettings by remember { mutableStateOf(false) }
    var feedPosts by remember { mutableStateOf(if (feedRepository == null) samplePosts else emptyList()) }
    var storyGroups by remember { mutableStateOf<List<StoryGroup>>(emptyList()) }
    var isLoading by remember { mutableStateOf(feedRepository != null) }

    // Feed settings state
    var syncSettings by remember { mutableStateOf(true) }
    var postsSettings by remember { mutableStateOf(FeedContentSettings()) }
    var shortsSettings by remember { mutableStateOf(FeedContentSettings()) }
    var showKeepWhichDialog by remember { mutableStateOf(false) }

    // Load posts + stories on first composition
    LaunchedEffect(feedRepository) {
        if (feedRepository == null) return@LaunchedEffect
        isLoading = true
        feedRepository.getFeed().onSuccess { feedPosts = it }
        isLoading = false
    }
    LaunchedEffect(storiesRepository) {
        storiesRepository?.getStoryGroups()?.onSuccess { storyGroups = it }
    }

    if (showCreateSheet) {
        CreatePostSheet(
            onDismiss = { showCreateSheet = false },
            onPost = { content, mediaUrls ->
                scope.launch {
                    if (feedRepository != null) {
                        feedRepository.createPost(content, mediaUrls).onSuccess {
                            // Reload feed to get server-assigned ID and timestamp
                            feedRepository.getFeed().onSuccess { posts -> feedPosts = posts }
                        }
                    } else {
                        feedPosts = listOf(
                            Post(
                                id = "new_${currentTimeMillis()}",
                                authorId = "me",
                                authorName = "You",
                                content = content,
                                timestamp = currentTimeMillis(),
                                imageUrls = mediaUrls
                            )
                        ) + feedPosts
                    }
                }
                showCreateSheet = false
            },
            onGoToShorts = {
                showCreateSheet = false
                selectedTab = 1
            }
        )
    }

    if (showFeedSettings) {
        FeedSettingsSheet(
            syncSettings = syncSettings,
            postsSettings = postsSettings,
            shortsSettings = shortsSettings,
            onSyncToggle = { newSync ->
                if (newSync) {
                    // Turning sync ON: check if settings differ
                    if (postsSettings != shortsSettings) {
                        showKeepWhichDialog = true
                    } else {
                        syncSettings = true
                    }
                } else {
                    // Turning sync OFF: initialize shorts from posts settings
                    shortsSettings = postsSettings
                    syncSettings = false
                }
            },
            onPostsSettingsChange = { newSettings ->
                postsSettings = newSettings
                if (syncSettings) shortsSettings = newSettings
            },
            onShortsSettingsChange = { newSettings ->
                shortsSettings = newSettings
            },
            onDismiss = { showFeedSettings = false }
        )
    }

    if (showKeepWhichDialog) {
        AlertDialog(
            onDismissRequest = { showKeepWhichDialog = false },
            title = { Text("Keep Which Settings?") },
            text = { Text("Your Posts and Shorts settings are different. Which settings would you like to keep for both?") },
            confirmButton = {
                TextButton(
                    onClick = {
                        shortsSettings = postsSettings
                        syncSettings = true
                        showKeepWhichDialog = false
                    }
                ) {
                    Text("Posts Settings")
                }
            },
            dismissButton = {
                TextButton(
                    onClick = {
                        postsSettings = shortsSettings
                        syncSettings = true
                        showKeepWhichDialog = false
                    }
                ) {
                    Text("Shorts Settings")
                }
            }
        )
    }

    Scaffold(
        topBar = {
            ModernTopBar(
                onSettingsClick = { showFeedSettings = true },
                onSearchClick = onSearchClick,
                onNotificationsClick = onNotificationsClick,
                onCreateClick = { if (isGuest) onAuthClick() else showCreateSheet = true },
                onAuthClick = onAuthClick,
                isGuest = isGuest
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
        ) {
            ModernTabRow(
                selectedTab = selectedTab,
                onTabSelected = { selectedTab = it }
            )
            when (selectedTab) {
                0 -> if (isLoading) {
                    Box(Modifier.fillMaxSize(), contentAlignment = androidx.compose.ui.Alignment.Center) {
                        CircularProgressIndicator()
                    }
                } else {
                    PostsFeed(
                        posts = feedPosts,
                        storyGroups = storyGroups,
                        onLike = { postId, liked ->
                            scope.launch {
                                if (liked) postRepository?.likePost(postId)
                                else postRepository?.unlikePost(postId)
                            }
                        }
                    )
                }
                1 -> ShortsFeed()
            }
        }
    }
}

// ============================================
// CREATE POST SHEET
// ============================================

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CreatePostSheet(
    onDismiss: () -> Unit,
    onPost: (String, List<String>) -> Unit,
    onGoToShorts: () -> Unit
) {
    var text by remember { mutableStateOf("") }
    // Tracks attached media (photo/video). In a real app these would be file URIs.
    // "photo" or "video" entries represent selected media stubs for UI purposes.
    var attachedMedia by remember { mutableStateOf<List<String>>(emptyList()) }
    var showMediaLimitWarning by remember { mutableStateOf(false) }

    ModalBottomSheet(onDismissRequest = onDismiss) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp)
                .padding(bottom = 32.dp)
        ) {
            // Header
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                TextButton(onClick = onDismiss) { Text("Cancel") }
                Spacer(Modifier.weight(1f))
                Text("New Post", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.titleMedium)
                Spacer(Modifier.weight(1f))
                Button(
                    onClick = {
                        if (text.isBlank()) {
                            onGoToShorts()
                        } else {
                            onPost(text.trim(), attachedMedia)
                        }
                    },
                    shape = RoundedCornerShape(20.dp)
                ) {
                    Text(if (text.isBlank()) "Short" else "Post")
                }
            }

            Spacer(Modifier.height(12.dp))

            // Text field
            OutlinedTextField(
                value = text,
                onValueChange = { text = it },
                placeholder = { Text("What's on your mind? (required for posts)") },
                modifier = Modifier
                    .fillMaxWidth()
                    .height(140.dp),
                maxLines = 6
            )

            // Text-required hint
            if (text.isBlank()) {
                Spacer(Modifier.height(6.dp))
                Text(
                    "No text? Your content will be posted as a Short instead.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            Spacer(Modifier.height(16.dp))

            // Attached media preview
            if (attachedMedia.isNotEmpty()) {
                LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    items(attachedMedia.size) { i ->
                        val isVideo = attachedMedia[i].startsWith("video")
                        Box(
                            modifier = Modifier
                                .size(72.dp)
                                .background(
                                    MaterialTheme.colorScheme.surfaceVariant,
                                    RoundedCornerShape(8.dp)
                                ),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(
                                if (isVideo) Icons.Filled.PlayCircle else Icons.Filled.Image,
                                contentDescription = null,
                                tint = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.size(32.dp)
                            )
                            // Remove button
                            IconButton(
                                onClick = { attachedMedia = attachedMedia.toMutableList().also { it.removeAt(i) } },
                                modifier = Modifier
                                    .align(Alignment.TopEnd)
                                    .size(24.dp)
                            ) {
                                Icon(
                                    Icons.Filled.Cancel,
                                    contentDescription = "Remove",
                                    tint = MaterialTheme.colorScheme.error,
                                    modifier = Modifier.size(18.dp)
                                )
                            }
                        }
                    }
                }
                Spacer(Modifier.height(12.dp))
            }

            if (showMediaLimitWarning) {
                Text(
                    "Videos must be 30 seconds or less.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.error
                )
                Spacer(Modifier.height(8.dp))
            }

            // Media action buttons
            Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                // Add photo
                OutlinedButton(
                    onClick = {
                        // In production: launch photo picker. For now, add a stub.
                        attachedMedia = attachedMedia + "photo_${attachedMedia.size}"
                        showMediaLimitWarning = false
                    },
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Icon(Icons.Filled.Image, contentDescription = null, modifier = Modifier.size(18.dp))
                    Spacer(Modifier.width(4.dp))
                    Text("Photo")
                }

                // Add video (≤30s)
                OutlinedButton(
                    onClick = {
                        // In production: launch video picker with 30s limit.
                        attachedMedia = attachedMedia + "video_${attachedMedia.size}"
                        showMediaLimitWarning = true
                    },
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Icon(Icons.Filled.VideoLibrary, contentDescription = null, modifier = Modifier.size(18.dp))
                    Spacer(Modifier.width(4.dp))
                    Text("Video ≤30s")
                }
            }
        }
    }
}

// ============================================
// MODERN TOP BAR
// ============================================

@Composable
fun ModernTopBar(
    onSettingsClick: () -> Unit,
    onSearchClick: () -> Unit,
    onNotificationsClick: () -> Unit,
    onCreateClick: () -> Unit,
    onAuthClick: () -> Unit = {},
    isGuest: Boolean = false
) {
    Surface(
        color = MaterialTheme.colorScheme.surface,
        tonalElevation = 0.dp
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 12.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Gradient app icon + brand name
            Row(verticalAlignment = Alignment.CenterVertically) {
                Box(
                    modifier = Modifier
                        .size(32.dp)
                        .background(
                            Brush.linearGradient(
                                colors = listOf(
                                    AppColors.GradientStart,
                                    AppColors.GradientMid,
                                    AppColors.GradientEnd,
                                    AppColors.GradientAccent
                                )
                            ),
                            RoundedCornerShape(8.dp)
                        )
                ) {
                    Icon(
                        Icons.Default.AutoAwesome,
                        contentDescription = null,
                        tint = Color.White,
                        modifier = Modifier
                            .align(Alignment.Center)
                            .size(20.dp)
                    )
                }
                Spacer(Modifier.width(12.dp))
                Text(
                    "Tangle",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = (-0.5).sp
                )
            }

            // Action icons
            Row(
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                if (isGuest) {
                    Button(
                        onClick = onAuthClick,
                        shape = RoundedCornerShape(20.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AppColors.GradientStart,
                            contentColor = Color.White
                        ),
                        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp),
                        modifier = Modifier.padding(end = 4.dp)
                    ) {
                        Text("Log In", style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.Bold)
                    }
                }

                IconButton(onClick = onCreateClick) {
                    Icon(
                        Icons.Outlined.AddBox,
                        contentDescription = "Create",
                        modifier = Modifier.size(26.dp)
                    )
                }
                IconButton(onClick = onSearchClick) {
                    Icon(
                        Icons.Outlined.Search,
                        contentDescription = "Search",
                        modifier = Modifier.size(26.dp)
                    )
                }
                IconButton(onClick = onNotificationsClick) {
                    Icon(
                        Icons.Default.FavoriteBorder,
                        contentDescription = "Notifications",
                        modifier = Modifier.size(26.dp)
                    )
                }
                IconButton(onClick = onSettingsClick) {
                    Icon(
                        Icons.Default.Settings,
                        contentDescription = "Feed Settings",
                        modifier = Modifier.size(26.dp)
                    )
                }
            }
        }
    }
}

// ============================================
// MODERN TAB ROW
// ============================================

@Composable
fun ModernTabRow(
    selectedTab: Int,
    onTabSelected: (Int) -> Unit
) {
    val tabs = listOf("Posts", "Shorts")
    val indicatorOffset by animateDpAsState(
        targetValue = if (selectedTab == 0) 0.dp else 150.dp,
        animationSpec = spring(
            dampingRatio = Spring.DampingRatioMediumBouncy,
            stiffness = Spring.StiffnessLow
        ),
        label = "tabIndicator"
    )

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(56.dp)
            .background(MaterialTheme.colorScheme.surface)
    ) {
        // Animated gradient indicator
        Box(
            modifier = Modifier
                .offset(x = indicatorOffset)
                .width(150.dp)
                .height(3.dp)
                .align(Alignment.BottomStart)
                .background(
                    Brush.horizontalGradient(
                        colors = listOf(AppColors.GradientStart, AppColors.GradientMid)
                    )
                )
        )

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceEvenly
        ) {
            tabs.forEachIndexed { index, title ->
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxHeight()
                        .clickable { onTabSelected(index) },
                    contentAlignment = Alignment.Center
                ) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center
                    ) {
                        Icon(
                            if (index == 0) Icons.Outlined.GridView else Icons.Outlined.VideoLibrary,
                            contentDescription = title,
                            tint = if (selectedTab == index) AppColors.GradientStart
                                   else MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.size(22.dp)
                        )
                        Spacer(Modifier.height(2.dp))
                        Text(
                            title,
                            style = MaterialTheme.typography.labelMedium,
                            fontWeight = if (selectedTab == index) FontWeight.Bold else FontWeight.Normal,
                            color = if (selectedTab == index) AppColors.GradientStart
                                    else MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }
            }
        }
    }
}

// ============================================
// POSTS FEED
// ============================================

@Composable
fun PostsFeed(
    posts: List<Post> = samplePosts,
    storyGroups: List<StoryGroup> = emptyList(),
    onLike: (postId: String, liked: Boolean) -> Unit = { _, _ -> }
) {
    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(bottom = 80.dp)
    ) {
        item { StoriesRow(storyGroups = storyGroups) }

        if (posts.isEmpty()) {
            item {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(vertical = 64.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        Icon(
                            Icons.Outlined.GridView,
                            contentDescription = null,
                            modifier = Modifier.size(56.dp),
                            tint = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Text(
                            "No posts yet",
                            style = MaterialTheme.typography.titleMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Text(
                            "Tap + to create the first post",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }
            }
        } else {
            items(posts, key = { it.id }) { post ->
                ModernPostCard(post = post, onLike = onLike)
                Spacer(Modifier.height(12.dp))
            }
        }
    }
}

// ============================================
// STORIES ROW
// ============================================

@Composable
fun StoriesRow(storyGroups: List<StoryGroup> = emptyList()) {
    LazyRow(
        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 12.dp),
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        // Add your story
        item {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier.width(90.dp)
            ) {
                Box(
                    modifier = Modifier
                        .size(width = 90.dp, height = 70.dp)
                        .clip(RoundedCornerShape(12.dp))
                        .background(MaterialTheme.colorScheme.surfaceVariant)
                        .clickable { },
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        Icons.Default.Add,
                        contentDescription = "Add Story",
                        modifier = Modifier.size(24.dp),
                        tint = AppColors.GradientStart
                    )
                }
                Text(
                    "Your Story",
                    style = MaterialTheme.typography.labelSmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.padding(top = 4.dp)
                )
            }
        }

        // Real story groups if available
        if (storyGroups.isNotEmpty()) {
            items(storyGroups) { group ->
                StoryAvatar(
                    username = group.username,
                    avatarUrl = group.userAvatar,
                    hasUnseen = group.hasUnseenStories
                )
            }
        }
    }
}

@Composable
private fun StoryAvatar(username: String, avatarUrl: String?, hasUnseen: Boolean) {
    val storyShape = RoundedCornerShape(12.dp)
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier.width(90.dp)
    ) {
        Box(
            modifier = Modifier
                .size(width = 90.dp, height = 70.dp)
                .then(
                    if (hasUnseen) Modifier.background(
                        Brush.linearGradient(
                            colors = listOf(AppColors.GradientStart, AppColors.GradientMid, AppColors.GradientEnd)
                        ),
                        storyShape
                    ).padding(3.dp) else Modifier.border(2.dp, MaterialTheme.colorScheme.surfaceVariant, storyShape)
                )
                .clickable { }
        ) {
            if (avatarUrl != null) {
                AsyncImage(
                    model = avatarUrl,
                    contentDescription = username,
                    modifier = Modifier.fillMaxSize().clip(storyShape),
                    contentScale = ContentScale.Crop
                )
            } else {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .background(MaterialTheme.colorScheme.surface, storyShape)
                        .padding(3.dp)
                        .clip(storyShape)
                        .background(MaterialTheme.colorScheme.primaryContainer),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        username.firstOrNull()?.uppercase() ?: "?",
                        style = MaterialTheme.typography.labelLarge,
                        color = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                }
            }
        }
        Text(
            username,
            style = MaterialTheme.typography.labelSmall,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.padding(top = 6.dp)
        )
    }
}

// ============================================
// MODERN POST CARD
// ============================================

@Composable
fun ModernPostCard(
    post: Post,
    onLike: (postId: String, liked: Boolean) -> Unit = { _, _ -> }
) {
    var isLiked by remember { mutableStateOf(post.isLikedByMe) }
    var isSaved by remember { mutableStateOf(false) }
    var likeCount by remember { mutableIntStateOf(post.likeCount) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(MaterialTheme.colorScheme.surface)
    ) {
        // Header
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Avatar with gradient border
            Box(
                modifier = Modifier
                    .size(40.dp)
                    .background(
                        Brush.linearGradient(
                            colors = listOf(AppColors.GradientStart, AppColors.GradientMid, AppColors.GradientEnd)
                        ),
                        CircleShape
                    )
                    .padding(2.dp)
            ) {
                if (post.authorAvatar != null) {
                    AsyncImage(
                        model = post.authorAvatar,
                        contentDescription = null,
                        modifier = Modifier
                            .fillMaxSize()
                            .background(MaterialTheme.colorScheme.surface, CircleShape)
                            .padding(2.dp)
                            .clip(CircleShape),
                        contentScale = ContentScale.Crop
                    )
                } else {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .background(MaterialTheme.colorScheme.surface, CircleShape)
                            .padding(2.dp)
                            .clip(CircleShape)
                            .background(MaterialTheme.colorScheme.primaryContainer),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            post.authorName.firstOrNull()?.uppercase() ?: "?",
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.onPrimaryContainer
                        )
                    }
                }
            }

            Spacer(Modifier.width(12.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    post.authorName,
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold
                )
                Text(
                    TimeFormatter.formatTimestamp(post.timestamp),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            IconButton(onClick = { }) {
                Icon(
                    Icons.Default.MoreVert,
                    contentDescription = "More"
                )
            }
        }

        // Image grid
        if (post.imageUrls.isNotEmpty()) {
            PostImageGrid(images = post.imageUrls)
        }

        // Caption
        if (post.content.isNotBlank()) {
            Text(
                post.content,
                modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp),
                style = MaterialTheme.typography.bodyMedium,
                maxLines = 3,
                overflow = TextOverflow.Ellipsis
            )
        }

        // Action buttons
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 4.dp, vertical = 4.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                // Like
                IconButton(onClick = {
                    val newLiked = !isLiked
                    isLiked = newLiked
                    likeCount = if (newLiked) likeCount + 1 else likeCount - 1
                    onLike(post.id, newLiked)
                }) {
                    Icon(
                        if (isLiked) Icons.Default.Favorite else Icons.Default.FavoriteBorder,
                        contentDescription = "Like",
                        modifier = Modifier.size(26.dp),
                        tint = if (isLiked) AppColors.GradientStart
                               else MaterialTheme.colorScheme.onSurface
                    )
                }
                // Comment
                IconButton(onClick = { }) {
                    Icon(
                        Icons.Default.ChatBubbleOutline,
                        contentDescription = "Comment",
                        modifier = Modifier.size(26.dp)
                    )
                }
                // Share
                IconButton(onClick = { }) {
                    Icon(
                        Icons.Outlined.Send,
                        contentDescription = "Share",
                        modifier = Modifier.size(26.dp)
                    )
                }
            }
            // Save
            IconButton(onClick = { isSaved = !isSaved }) {
                Icon(
                    if (isSaved) Icons.Default.Bookmark else Icons.Default.BookmarkBorder,
                    contentDescription = "Save",
                    modifier = Modifier.size(26.dp)
                )
            }
        }

        // Like count
        if (likeCount > 0) {
            Text(
                "$likeCount likes",
                modifier = Modifier.padding(horizontal = 16.dp),
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.Bold
            )
        }

        // Comment count link
        if (post.commentCount > 0) {
            Text(
                "View all ${post.commentCount} comments",
                modifier = Modifier
                    .padding(horizontal = 16.dp, vertical = 4.dp)
                    .clickable { },
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }

        Spacer(Modifier.height(4.dp))
    }
}

// ============================================
// POST IMAGE GRID
// ============================================

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun PostImageGrid(images: List<String>) {
    when (images.size) {
        1 -> Box(
            modifier = Modifier
                .fillMaxWidth()
                .aspectRatio(1f)
                .background(Color.Black)
        ) {
            AsyncImage(
                model = images[0],
                contentDescription = null,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Crop
            )
        }
        2 -> Row(
            Modifier
                .fillMaxWidth()
                .height(300.dp)
        ) {
            images.forEach { url ->
                AsyncImage(
                    model = url,
                    contentDescription = null,
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxHeight(),
                    contentScale = ContentScale.Crop
                )
            }
        }
        else -> LazyVerticalStaggeredGrid(
            columns = StaggeredGridCells.Fixed(2),
            modifier = Modifier
                .fillMaxWidth()
                .height(400.dp),
            horizontalArrangement = Arrangement.spacedBy(2.dp),
            verticalItemSpacing = 2.dp
        ) {
            items(images.take(4)) { url ->
                AsyncImage(
                    model = url,
                    contentDescription = null,
                    modifier = Modifier
                        .fillMaxWidth()
                        .aspectRatio(if (images.indexOf(url) == 0) 1f else 0.7f),
                    contentScale = ContentScale.Crop
                )
            }
        }
    }
}

// ============================================
// SHORTS FEED (TikTok-style vertical video)
// ============================================

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun ShortsFeed(shorts: List<Short> = sampleShorts) {
    if (shorts.isEmpty()) {
        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Icon(
                    Icons.Outlined.VideoLibrary,
                    contentDescription = null,
                    modifier = Modifier.size(56.dp),
                    tint = MaterialTheme.colorScheme.onSurfaceVariant
                )
                Text(
                    "No shorts yet",
                    style = MaterialTheme.typography.titleMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                Text(
                    "Be the first to share a short video",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
        return
    }

    val pagerState = rememberPagerState(pageCount = { shorts.size })

    VerticalPager(
        state = pagerState,
        modifier = Modifier.fillMaxSize()
    ) { page ->
        ShortCard(short = shorts[page])
    }
}

@Composable
fun ShortCard(short: Short) {
    var isLiked by remember { mutableStateOf(false) }
    var isFollowing by remember { mutableStateOf(false) }
    var likeCount by remember { mutableIntStateOf(short.likeCount) }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black)
    ) {
        // Video thumbnail (full screen)
        AsyncImage(
            model = short.thumbnailUrl,
            contentDescription = null,
            modifier = Modifier.fillMaxSize(),
            contentScale = ContentScale.Crop
        )

        // Dark gradient overlay
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(
                    Brush.verticalGradient(
                        colors = listOf(Color.Transparent, Color.Black.copy(alpha = 0.7f)),
                        startY = 400f
                    )
                )
        )

        // Right side actions (TikTok style)
        Column(
            modifier = Modifier
                .align(Alignment.CenterEnd)
                .padding(end = 12.dp, bottom = 80.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(24.dp)
        ) {
            // Like
            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                IconButton(
                    onClick = {
                        isLiked = !isLiked
                        likeCount = if (isLiked) likeCount + 1 else likeCount - 1
                    },
                    modifier = Modifier.size(48.dp)
                ) {
                    Icon(
                        if (isLiked) Icons.Default.Favorite else Icons.Default.FavoriteBorder,
                        contentDescription = "Like",
                        tint = if (isLiked) AppColors.GradientStart else Color.White,
                        modifier = Modifier.size(32.dp)
                    )
                }
                Text(
                    formatCount(likeCount),
                    color = Color.White,
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Bold
                )
            }

            // Comment
            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                IconButton(
                    onClick = { },
                    modifier = Modifier.size(48.dp)
                ) {
                    Icon(
                        Icons.Default.ChatBubbleOutline,
                        contentDescription = "Comment",
                        tint = Color.White,
                        modifier = Modifier.size(32.dp)
                    )
                }
                Text(
                    formatCount(short.commentCount),
                    color = Color.White,
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Bold
                )
            }

            // Share
            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                IconButton(
                    onClick = { },
                    modifier = Modifier.size(48.dp)
                ) {
                    Icon(
                        Icons.Outlined.Send,
                        contentDescription = "Share",
                        tint = Color.White,
                        modifier = Modifier.size(32.dp)
                    )
                }
                Text(
                    formatCount(short.shareCount),
                    color = Color.White,
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Bold
                )
            }

            // Save
            IconButton(
                onClick = { },
                modifier = Modifier.size(48.dp)
            ) {
                Icon(
                    Icons.Default.BookmarkBorder,
                    contentDescription = "Save",
                    tint = Color.White,
                    modifier = Modifier.size(32.dp)
                )
            }
        }

        // Bottom info overlay
        Column(
            modifier = Modifier
                .align(Alignment.BottomStart)
                .padding(16.dp)
                .padding(bottom = 80.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.padding(bottom = 12.dp)
            ) {
                Box(
                    modifier = Modifier
                        .size(40.dp)
                        .clip(CircleShape)
                        .background(MaterialTheme.colorScheme.primaryContainer)
                )
                Spacer(Modifier.width(12.dp))
                Text(
                    short.username,
                    color = Color.White,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold
                )
                Spacer(Modifier.width(12.dp))
                if (!isFollowing) {
                    Button(
                        onClick = { isFollowing = true },
                        colors = ButtonDefaults.buttonColors(containerColor = AppColors.GradientStart),
                        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 6.dp),
                        modifier = Modifier.height(32.dp)
                    ) {
                        Text(
                            "Follow",
                            style = MaterialTheme.typography.labelMedium,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            }

            Text(
                short.caption,
                color = Color.White,
                style = MaterialTheme.typography.bodyMedium,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis
            )

            if (short.tags.isNotEmpty()) {
                Spacer(Modifier.height(8.dp))
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    short.tags.take(3).forEach { tag ->
                        Text(
                            "#$tag",
                            color = Color.White,
                            style = MaterialTheme.typography.labelMedium,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            }

            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.padding(top = 8.dp)
            ) {
                Icon(
                    Icons.Default.MusicNote,
                    contentDescription = null,
                    tint = Color.White,
                    modifier = Modifier.size(16.dp)
                )
                Spacer(Modifier.width(6.dp))
                Text(
                    short.audioName,
                    color = Color.White,
                    style = MaterialTheme.typography.labelSmall
                )
            }
        }
    }
}

// ============================================
// FEED CONTENT SETTINGS
// ============================================

data class FeedContentSettings(
    val showImages: Boolean = true,
    val showTextPosts: Boolean = true,
    val showReposts: Boolean = true,
    val autoplayVideos: Boolean = true,
    val showSuggested: Boolean = true,
    val selectedTags: Set<String> = emptySet()
)

private val feedContentTags = listOf(
    "Technology", "Gaming", "Music", "Art", "Photography",
    "Travel", "Food", "Sports", "Fashion", "Science",
    "Health", "Fitness", "Finance", "Education", "Comedy",
    "News", "Lifestyle", "Beauty", "Automotive", "Pets",
    "Politics", "DIY", "Nature", "Books", "Film"
)

// ============================================
// FEED SETTINGS SHEET
// ============================================

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun FeedSettingsSheet(
    syncSettings: Boolean,
    postsSettings: FeedContentSettings,
    shortsSettings: FeedContentSettings,
    onSyncToggle: (Boolean) -> Unit,
    onPostsSettingsChange: (FeedContentSettings) -> Unit,
    onShortsSettingsChange: (FeedContentSettings) -> Unit,
    onDismiss: () -> Unit
) {
    var selectedSettingsTab by remember { mutableStateOf(0) }

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = 16.dp)
                .padding(bottom = 40.dp)
        ) {
            // Header
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    "Feed Settings",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.weight(1f)
                )
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Default.Close, contentDescription = "Close")
                }
            }

            Spacer(Modifier.height(12.dp))

            // Sync toggle
            Surface(
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(12.dp),
                color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f)
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { onSyncToggle(!syncSettings) }
                        .padding(horizontal = 16.dp, vertical = 14.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(
                        modifier = Modifier
                            .size(36.dp)
                            .background(AppColors.GradientStart, RoundedCornerShape(8.dp)),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            Icons.Default.Sync,
                            contentDescription = null,
                            tint = Color.White,
                            modifier = Modifier.size(20.dp)
                        )
                    }
                    Spacer(Modifier.width(12.dp))
                    Column(modifier = Modifier.weight(1f)) {
                        Text("Sync Posts & Shorts", style = MaterialTheme.typography.bodyLarge)
                        Text(
                            if (syncSettings) "Same settings for both" else "Different settings for each",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                    Switch(
                        checked = syncSettings,
                        onCheckedChange = { onSyncToggle(it) },
                        colors = SwitchDefaults.colors(
                            checkedThumbColor = Color.White,
                            checkedTrackColor = AppColors.GradientStart
                        )
                    )
                }
            }

            Spacer(Modifier.height(16.dp))

            // Posts / Shorts tab selector when not synced
            if (!syncSettings) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(
                            MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f),
                            RoundedCornerShape(12.dp)
                        )
                        .padding(4.dp)
                ) {
                    listOf("Posts", "Shorts").forEachIndexed { index, label ->
                        Box(
                            modifier = Modifier
                                .weight(1f)
                                .clip(RoundedCornerShape(10.dp))
                                .background(
                                    if (selectedSettingsTab == index) MaterialTheme.colorScheme.surface
                                    else Color.Transparent
                                )
                                .clickable { selectedSettingsTab = index }
                                .padding(vertical = 10.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                label,
                                style = MaterialTheme.typography.labelLarge,
                                fontWeight = if (selectedSettingsTab == index) FontWeight.Bold else FontWeight.Normal,
                                color = if (selectedSettingsTab == index) AppColors.GradientStart
                                        else MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
                Spacer(Modifier.height(16.dp))
            }

            // Settings panel for the active section
            val currentSettings = if (syncSettings || selectedSettingsTab == 0) postsSettings else shortsSettings
            val onSettingsChange: (FeedContentSettings) -> Unit =
                if (syncSettings || selectedSettingsTab == 0) onPostsSettingsChange else onShortsSettingsChange

            ContentSettingsPanel(
                settings = currentSettings,
                onSettingsChange = onSettingsChange
            )
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ContentSettingsPanel(
    settings: FeedContentSettings,
    onSettingsChange: (FeedContentSettings) -> Unit
) {
    // Content toggles section header
    Text(
        "CONTENT",
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.primary,
        fontWeight = FontWeight.SemiBold,
        letterSpacing = 0.5.sp,
        modifier = Modifier.padding(bottom = 8.dp, start = 4.dp)
    )

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f)
    ) {
        Column {
            ContentToggleRow(
                icon = Icons.Outlined.Image,
                iconColor = Color(0xFF2196F3),
                label = "Show Images",
                checked = settings.showImages,
                onCheckedChange = { onSettingsChange(settings.copy(showImages = it)) }
            )
            HorizontalDivider(modifier = Modifier.padding(start = 64.dp), thickness = 0.5.dp)
            ContentToggleRow(
                icon = Icons.Outlined.TextFields,
                iconColor = Color(0xFF4CAF50),
                label = "Show Text Posts",
                checked = settings.showTextPosts,
                onCheckedChange = { onSettingsChange(settings.copy(showTextPosts = it)) }
            )
            HorizontalDivider(modifier = Modifier.padding(start = 64.dp), thickness = 0.5.dp)
            ContentToggleRow(
                icon = Icons.Default.Repeat,
                iconColor = Color(0xFF9C27B0),
                label = "Show Reposts",
                checked = settings.showReposts,
                onCheckedChange = { onSettingsChange(settings.copy(showReposts = it)) }
            )
            HorizontalDivider(modifier = Modifier.padding(start = 64.dp), thickness = 0.5.dp)
            ContentToggleRow(
                icon = Icons.Outlined.PlayArrow,
                iconColor = Color(0xFFFF9800),
                label = "Autoplay Videos",
                checked = settings.autoplayVideos,
                onCheckedChange = { onSettingsChange(settings.copy(autoplayVideos = it)) }
            )
            HorizontalDivider(modifier = Modifier.padding(start = 64.dp), thickness = 0.5.dp)
            ContentToggleRow(
                icon = Icons.Outlined.Explore,
                iconColor = Color(0xFFE1306C),
                label = "Show Suggested Content",
                checked = settings.showSuggested,
                onCheckedChange = { onSettingsChange(settings.copy(showSuggested = it)) }
            )
        }
    }

    Spacer(Modifier.height(20.dp))

    // Tags section
    Text(
        "TAGS",
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.primary,
        fontWeight = FontWeight.SemiBold,
        letterSpacing = 0.5.sp,
        modifier = Modifier.padding(bottom = 4.dp, start = 4.dp)
    )
    Text(
        "Select topics to prioritize in your feed",
        style = MaterialTheme.typography.bodySmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        modifier = Modifier.padding(bottom = 12.dp, start = 4.dp)
    )

    FlowRow(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        feedContentTags.forEach { tag ->
            val selected = tag in settings.selectedTags
            FilterChip(
                selected = selected,
                onClick = {
                    val newTags = if (selected) settings.selectedTags - tag else settings.selectedTags + tag
                    onSettingsChange(settings.copy(selectedTags = newTags))
                },
                label = { Text(tag, style = MaterialTheme.typography.labelMedium) },
                colors = FilterChipDefaults.filterChipColors(
                    selectedContainerColor = AppColors.GradientStart.copy(alpha = 0.15f),
                    selectedLabelColor = AppColors.GradientStart
                ),
                border = FilterChipDefaults.filterChipBorder(
                    enabled = true,
                    selected = selected,
                    selectedBorderColor = AppColors.GradientStart
                )
            )
        }
    }
}

@Composable
private fun ContentToggleRow(
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    iconColor: Color,
    label: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onCheckedChange(!checked) }
            .padding(horizontal = 16.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(36.dp)
                .background(iconColor, RoundedCornerShape(8.dp)),
            contentAlignment = Alignment.Center
        ) {
            Icon(icon, contentDescription = null, tint = Color.White, modifier = Modifier.size(20.dp))
        }
        Spacer(Modifier.width(12.dp))
        Text(label, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f))
        Switch(
            checked = checked,
            onCheckedChange = onCheckedChange,
            colors = SwitchDefaults.colors(
                checkedThumbColor = Color.White,
                checkedTrackColor = Color(0xFF4CAF50)
            )
        )
    }
}

// ============================================
// HELPERS
// ============================================

fun formatCount(count: Int): String = when {
    count >= 1_000_000 -> "${count / 1_000_000}M"
    count >= 1_000 -> "${count / 1_000}K"
    else -> count.toString()
}

// ============================================
// SAMPLE DATA
// ============================================

val samplePosts = emptyList<Post>()

val sampleShorts = emptyList<Short>()
